import type { Account } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  BolusState,
  DataSource,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import {
  useMemo,
  useRef,
} from "react"

import { useToday } from "src/hooks/time"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import type {
  ReadOnlyUserEvent,
  ReadOnlyUserEntry,
} from "src/types"
import {
  createEventQueryObject,
  createEntryQueryObject,
} from "src/utils"

export const ongoingSectionId = "ongoingSectionId"

export type ActivityItem = {
  activity: ReadOnlyUserEvent | ReadOnlyUserEntry;
  isFirst: boolean;
  isLast: boolean;
}

interface ActivityGroup {
  date: string;
  data: (ReadOnlyUserEvent | ReadOnlyUserEntry)[];
}

export type FeedItem = string | ActivityItem

/*
 * returns an array of read only user events and entries except for glucose events
 */
export function useSortedFeedItems(accountId: Account["id"]): FeedItem[] {
  const today = useToday()
  const ninetyDaysAgo = useMemo(
    () => dayjs(today)
      .subtract(
        90,
        "days",
      )
      .startOf("day")
      .toISOString(),
    [today],
  )

  const entriesQueryObject = useMemo(
    () => createEntryQueryObject(
      accountId,
      {
        selector: {
          started_at: { $gte: ninetyDaysAgo },
        },
      },
    ),
    [
      accountId,
      ninetyDaysAgo,
    ],
  )

  const {
    result: entries,
    isFetching: isFetchingEntries,
  } = useFindUserEntries(entriesQueryObject)

  const eventsQueryObject = useMemo(
    () => createEventQueryObject(
      accountId,
      {
        selector: {
          $or: [
            {
              source: {
                $in: [
                  DataSource.AppleHealthKit,
                  DataSource.AndroidHealthConnect,
                ],
              },
            },
            {
              event_type: {
                $in: [
                  ActivityType.Bolus,
                  ActivityType.DeviceChange,
                  ActivityType.PumpSuspension,
                  ActivityType.CGMSessionEnded,
                ],
              },
            },
          ],
          started_at: { $gte: ninetyDaysAgo },
        },
      },
    ),
    [
      accountId,
      ninetyDaysAgo,
    ],
  )

  const {
    result: events,
    isFetching: isFetchingEvents,
  } = useFindUserEvents(eventsQueryObject)

  const lastReturnedFeedItems = useRef<FeedItem[]>([getDate(today)])

  return useMemo(
    (): FeedItem[] => {
      if (isFetchingEntries || isFetchingEvents) {
        return lastReturnedFeedItems.current
      }

      const todayDate = getDate(today)

      const ongoingActivityGroup: ActivityGroup = {
        date: ongoingSectionId,
        data: [],
      }

      const accumulator: Record<string, ActivityGroup> = {
        [todayDate]: {
          date: todayDate,
          data: [],
        },
      }

      let nextEventIndex = 0
      let nextEntryIndex = 0

      while (nextEventIndex + nextEntryIndex < events.length + entries.length) {
      // get the most recent event or entry that hasn't been added yet
        const nextEvent = events[nextEventIndex]
        const nextEntry = entries[nextEntryIndex]

        if (!nextEvent && !nextEntry) {
          break
        }

        // collect any ongoing pump suspension  & bolus events and put them in the "ongoing" bucket
        if (
          nextEvent &&
          ((nextEvent.event_type === ActivityType.PumpSuspension &&
            nextEvent.pump_suspension.state === PumpSuspendState.InProgress) ||
           (nextEvent.event_type === ActivityType.Bolus &&
            nextEvent.bolus.state === BolusState.InProgress))
        ) {
          ongoingActivityGroup.data.push(nextEvent)
          nextEventIndex += 1
          continue
        }

        // Skip initiated bolus events.
        if (
          nextEvent &&
          nextEvent.event_type === ActivityType.Bolus &&
          nextEvent.bolus.state === BolusState.Initiated
        ) {
          nextEventIndex += 1
          continue
        }

        const nextActivity: ReadOnlyUserEvent | ReadOnlyUserEntry =
          nextEvent && nextEntry
            ? dayjs(nextEvent.started_at).isAfter(nextEntry.started_at)
              ? nextEvent
              : nextEntry
            : (nextEvent ?? nextEntry)

        if (nextActivity === nextEvent) {
          nextEventIndex += 1
        }

        if (nextActivity === nextEntry) {
          nextEntryIndex += 1
        }

        // add the next item to the relevant section
        const date = getDate(nextActivity.started_at)

        if (!accumulator[date]) {
          accumulator[date] = {
            date,
            data: [],
          }
        }

        accumulator[date].data.push(nextActivity)
      }

      const sortedGroups = Object.values(accumulator).sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))

      // Add the ongoing section to the top of the feed if there are ongoing events
      if (ongoingActivityGroup.data.length) {
        sortedGroups.unshift(ongoingActivityGroup)
      }

      const items = sortedGroups
        .map((group: ActivityGroup) => [
          group.date,
          group.data.map((activity, index) => ({
            activity,
            isFirst: index === 0,
            isLast: index === group.data.length - 1,
          })),
        ])
        .flat(2)

      lastReturnedFeedItems.current = items
      return items
    },
    [
      today,
      entries,
      events,
      isFetchingEntries,
      isFetchingEvents,
    ],
  )
}

function getDate(started_at: string) {
  return dayjs(started_at).startOf("day")
    .format("YYYY-MM-DD")
}
